import { WebhooksTypes } from './types';
import { makeEventsKey } from './utils';
import {
  WebhookEventOption,
  WebhookSubscriptionError,
  TestEndpointResult,
} from '@fullstory/client-api/goimports/fs_services_webber_internal_webhooks';
import { createRequestReducer } from '@fullstory/redux-util';
import { WebhooksActions } from './actions';
import {
  DeliveryAttempt,
  FetchWebhookEventResponse,
  Subscription,
} from '@fullstory/proto/fs/services/capnhook';
import { combineReducers } from '@fullstory/redux-util/utils/combineReducers';

const subscriptions = createRequestReducer<Subscription[]>()(
  WebhooksTypes.FetchSubscriptionsRequest,
  WebhooksTypes.FetchSubscriptionsSuccess,
  WebhooksTypes.FetchSubscriptionsFailure,
);

const events = createRequestReducer<WebhookEventOption[]>()(
  WebhooksTypes.FetchEventsRequest,
  WebhooksTypes.FetchEventsSuccess,
  WebhooksTypes.FetchEventsFailure,
);

const errors = createRequestReducer<{ [url: string]: WebhookSubscriptionError[] }>()(
  WebhooksTypes.FetchErrorsRequest,
  WebhooksTypes.FetchErrorsSuccess,
  WebhooksTypes.FetchErrorsFailure,
);

const testResults = createRequestReducer<TestEndpointResult>()(
  WebhooksTypes.TestEndpointRequest,
  WebhooksTypes.TestEndpointSuccess,
  WebhooksTypes.TestEndpointFailure,
);

const deliveryAttempts = (
  state: DeliveryAttempt[] = [],
  action: WebhooksActions,
): DeliveryAttempt[] => {
  switch (action.type) {
    case WebhooksTypes.FetchDeliveryAttemptsSuccess: {
      const body = action.request.body;
      if (!body) {
        throw Error(
          `request.body is ${action.request.body} in WebhooksTypes.FetchDeliveryAttemptsSuccess`,
        );
      }
      // If the request had a pagination token, then we must be fetching more attempts of the same url and filters.
      return body.paginationToken !== ''
        ? [...state, ...action.data.attempts]
        : action.data.attempts;
    }
    default:
      return state;
  }
};

const webhookEvents = (
  state: { [key: string]: FetchWebhookEventResponse } = {},
  action: WebhooksActions,
): { [key: string]: FetchWebhookEventResponse } => {
  switch (action.type) {
    case WebhooksTypes.FetchWebhookEventSuccess: {
      const body = action.request.body;
      if (!body) {
        throw Error(
          `request.body is ${action.request.body} in WebhooksTypes.FetchWebhookEventSuccess`,
        );
      }
      const key = makeEventsKey(body.eventId, body.version);
      return {
        ...state,
        [key]: action.data,
      };
    }
    default:
      return state;
  }
};

const nextPaginationToken = (state: string = '', action: WebhooksActions): string => {
  switch (action.type) {
    case WebhooksTypes.FetchDeliveryAttemptsSuccess:
      return action.data.nextPaginationToken;
    default:
      return state;
  }
};

type WebhooksReducerState = {
  subscriptions: ReturnType<typeof subscriptions>;
  events: ReturnType<typeof events>;
  errors: ReturnType<typeof errors>;
  testResults: ReturnType<typeof testResults>;
  deliveryAttempts: ReturnType<typeof deliveryAttempts>;
  webhookEvents: ReturnType<typeof webhookEvents>;
  nextPaginationToken: ReturnType<typeof nextPaginationToken>;
};

export const webhooksReducer = combineReducers<WebhooksReducerState>({
  subscriptions,
  events,
  errors,
  testResults,
  deliveryAttempts,
  webhookEvents,
  nextPaginationToken,
});
